import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, DollarSign } from "lucide-react";
import KanbanBoard from "@/components/KanbanBoard";

const COLUMNS = [
  { id: "entrou", label: "Veículo Entrou", accent: "bg-slate-500" },
  { id: "em_inspecao", label: "Em Inspeção", accent: "bg-amber-500" },
  { id: "aguardando_estimate", label: "Aguardando Estimate", accent: "bg-neutral-500" },
  { id: "aguardando_aprovacao", label: "Aguardando Aprovação", accent: "bg-orange-500" },
  { id: "aguardando_pecas", label: "Aguardando Peças", accent: "bg-purple-500" },
  { id: "em_andamento", label: "Serviço em Andamento", accent: "bg-primary" },
  { id: "pronto", label: "Serviço Pronto", accent: "bg-emerald-500" },
  { id: "entregue", label: "Veículo Entregue", accent: "bg-neutral-500" },
];

export default function KanbanOficina() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/jobs", { params: { job_type: "mechanic" } });
    setJobs(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onStatusChange = async (jobId, newStatus) => {
    await api.patch(`/jobs/${jobId}/status`, { status: newStatus });
  };

  const renderExtra = null;

  return (
    <div className="space-y-4 -mt-2" data-testid="kanban-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Fluxo de trabalho</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Kanban Oficina</h1>
          <p className="text-sm text-muted-foreground mt-1">Arraste cards entre colunas para atualizar o status.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-job-btn"><Plus className="w-4 h-4" /> Novo Job</Button>
          </DialogTrigger>
          <NovoJobDialog onCreated={() => { setOpen(false); load(); }} />
        </Dialog>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Carregando…</div> :
        <KanbanBoard columns={COLUMNS} jobs={jobs} onStatusChange={onStatusChange} onJobsChanged={load} />}
    </div>
  );
}

function NovoJobDialog({ onCreated }) {
  const [form, setForm] = useState({ vehicle_id: "", client_id: "", service_type: "", technician: "", priority: "normal", notes: "", status: "entrou", job_type: "mechanic" });
  const [vehicles, setVehicles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/vehicles").then(({ data }) => setVehicles(data)); }, []);

  const onVehicleChange = (vid) => {
    const v = vehicles.find((x) => x.id === vid);
    setForm((f) => ({ ...f, vehicle_id: vid, client_id: v?.client_id || "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_id || !form.service_type) { toast.error("Veículo e serviço são obrigatórios"); return; }
    setSaving(true);
    try {
      await api.post("/jobs", form);
      toast.success("Job criado");
      onCreated();
    } catch { toast.error("Erro ao criar"); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="sm:max-w-lg" data-testid="new-job-dialog">
      <DialogHeader><DialogTitle className="font-display tracking-tight">Novo Job</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <Label>Veículo *</Label>
          <Select value={form.vehicle_id} onValueChange={onVehicleChange}>
            <SelectTrigger data-testid="job-vehicle-select"><SelectValue placeholder="Selecionar veículo" /></SelectTrigger>
            <SelectContent>
              {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model} · {v.plate || v.vin?.slice(-6)} · {v.client_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Tipo de Serviço *</Label>
          <Input value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })} placeholder="Ex: Troca de óleo" data-testid="job-service-input" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Técnico Responsável</Label>
            <Input value={form.technician} onChange={(e) => setForm({ ...form, technician: e.target.value })} data-testid="job-technician-input" />
          </div>
          <div>
            <Label>Prioridade</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="urgente">Urgente</SelectItem>
                <SelectItem value="importante">Importante</SelectItem>
                <SelectItem value="monitorar">Monitorar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label>Notas</Label>
          <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <DialogFooter>
          <Button type="submit" disabled={saving} data-testid="job-save-btn">{saving ? "Salvando…" : "Criar Job"}</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
